import { Box, HStack, NumberDecrementStepper, NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper, Stack, Switch, Text, Textarea, useToast, VStack, Wrap, WrapItem } from "@chakra-ui/react";
import { ChangeEvent, useState } from "react";

import { $count, $ok, $unsigned } from "foundation-ts/commons";
import { $timeBetweenDates } from "foundation-ts/date";
import { $trim } from "foundation-ts/strings";
import { TSDate, TSDay } from "foundation-ts/tsdate";
import { TSInterval } from "foundation-ts/tsinterval";
import { Descending, Nullable, UUID } from "foundation-ts/types";
import { SessionDto, SignatureDto, UserDto } from "doxecure-commons/lib/doxecureClientTypes";

import { UserCard } from "@components/Cards/UserCard";
import { G1Input } from "@components/G1Input";
import { G1DateInput } from "@components/G1DateInput";
import { SeaShellBox } from "@components/SeaShellBox";
import { SectionBox } from "@components/SectionBox/SectionBox";
import { SectionBoxFooter } from "@components/SectionBox/SectionBoxFooter";
import { config } from "@/config/config";
import { useStyles } from "@hooks/useStyles";
import { $g1Color } from "@/theme/G1Style";
import { G1Icon } from "@utils/icons";
import { optlog } from "@utils/functions";
import { localformat, localtrans } from "@utils/LocalTranslations";
import { defaultErrorToast, defaultInfoToast } from "@utils/toast";
import { NeededPersonRole } from "@utils/TypesAndConstants";
import { SignaturesManager } from "@/components/SignersManager/SignaturesManager";

interface SignatureBoxProps {
    session: SessionDto;
    displayCalendar?: boolean;
    displayDuringTime?: boolean;
    optionAddApproversSwitchChecked?: boolean;
    optionInvisibleSwitchChecked?: boolean;
    optionOneToManyModeSwitchChecked?: boolean;
    enableAddApproversOption?: boolean;
    onAddUser: (role: NeededPersonRole) => void;
    onChangeSession: (oldSession:SessionDto, newSession:SessionDto) => void;
    onRemoveUser: (apid: UUID, role: NeededPersonRole) => void;
    onToggleAddApproversSwitch: () => void;
    onToggleInvisibleSwitch: () => void;
    onToggleOneToManyModeSwitch: () => void;
}

const MIN_DURING_TIME = 3 ;  // in days
export const MAX_DURING_TIME = 30 ; // idem

export const SignatureBox = (props: SignatureBoxProps) => {
    const { session, displayCalendar, displayDuringTime, optionAddApproversSwitchChecked, optionInvisibleSwitchChecked, optionOneToManyModeSwitchChecked, enableAddApproversOption, onAddUser, onRemoveUser, onChangeSession, onToggleAddApproversSwitch, onToggleInvisibleSwitch, onToggleOneToManyModeSwitch } = props;
    const { sectionStyle, itemStyle } = useStyles();
    const [duringTime, setDuringTime] = useState($ok(session) ? ($timeBetweenDates(session.validity.start.dateWithoutTime(), session.validity.end.dateWithoutTime()) / TSDay) : MIN_DURING_TIME) ;
    const toast = useToast() ;
    const endDate = session?.validity?.end?.dateWithoutTime() ;
    const startDate = session?.validity?.start?.dateWithoutTime() ;

    const onChangeTitle = (e: ChangeEvent<HTMLInputElement>) => {
        let value = $trim(e.target.value) ;
        if (value !== session.title) {
            const newSession:SessionDto = {
                ...session!,
                title: value
            };
            onChangeSession(session, newSession) ;
        }
    };

    const onChangeObject = (e: ChangeEvent<HTMLTextAreaElement>) => {
        let value = $trim(e.target.value) ;
        if (value !== session.description) {
            const newSession:SessionDto = {
                ...session!,
                description: value
            };
            onChangeSession(session, newSession) ;
        }
    };

    const onChangeDate = (newEnd:Nullable<TSDate>):TSDate|undefined => {
        optlog(`Did get new date value ${newEnd})`) ;
        const start = session?.validity?.start ;
        if ($ok(start) && $ok(newEnd)) {
            if (newEnd!.compare(start) !== Descending) {
                toast(defaultErrorToast("La date de fin de validité d'une session doit être ultérieure à la date de début de validité"));
                return session?.validity?.end ;
            } else if ($timeBetweenDates(start.dateWithoutTime(), newEnd) < MIN_DURING_TIME*TSDay) {
                toast(defaultErrorToast(localformat("SessionMinimalDurationError", { days:MAX_DURING_TIME })));
                return session?.validity?.end ;
            } else {
                const newSession:SessionDto = {
                    ...session!,
                    validity: new TSInterval(start, newEnd)
                };
                onChangeSession(session, newSession) ;
            }
        }
        return undefined ;
    }

    const onChangeDuringTime = (_valueAsString: string, valueAsNumber: number) => {
        const newDuringTime = (valueAsNumber < MIN_DURING_TIME) ? MIN_DURING_TIME : (valueAsNumber > MAX_DURING_TIME ? MAX_DURING_TIME : valueAsNumber) ;
        setDuringTime(newDuringTime) ;
        const now = TSDate.zulu() ;
        const newSession:SessionDto = {
            ...session!,
            validity: new TSInterval(now, now.dateByAddingDays(newDuringTime))
        };
        onChangeSession(session, newSession) ;
    }

    const onChangeSignatures = (signatures: SignatureDto[]) => {
        const newSession: SessionDto = {
            ...session!,
            signatures
        };
        onChangeSession(session, newSession);
    }

    const onRemoveSigner = (apid: UUID) => {
        onRemoveUser(apid, NeededPersonRole.Signer);
    }

    const _renderSenderItem = (user: UserDto) => {
        return (
            <UserCard key={user.apid} item={user} itemStyle={itemStyle.expeditor} onItemClick={() => onAddUser(NeededPersonRole.Sender)} />
        );
    };

    const _renderApproverItem = (user: UserDto, parentSession?: SessionDto, index?: number) => {
        const N = $count(parentSession?.approvals);
        const I = $unsigned(index);

        const up = () => {
            if ($count(parentSession?.approvals) === 0) { return; }
            const approvals = [...parentSession!.approvals!];
            [approvals[I-1], approvals[I]] =  [approvals[I], approvals[I-1]];
            onChangeSession(session, { ...session, approvals });
        }

        const down = () => {
            if ($count(parentSession?.approvals) === 0) { return; }
            const approvals = [...parentSession!.approvals!];
            [approvals[I], approvals[I+1]] =  [approvals[I+1], approvals[I]];
            onChangeSession(session, { ...session, approvals });
        }

        const renderUpDownButtons = () => {
            return (
                <Stack align="stretch" alignSelf="center">
                    {I > 0 && (
                        <Box margin="5%">
                            <G1Icon.Up size="20px" cursor="pointer" onClick={up} />
                        </Box>
                    )}
                    {I + 1 < N && (
                        <Box margin="5%">
                            <G1Icon.Down size="20px" cursor="pointer" onClick={down} />
                        </Box>
                    )}
                </Stack>
            );
        };

        return (
            <UserCard
                key={user.apid}
                displayIcon={true}
                item={user}
                itemStyle={itemStyle.signer}
                onRemoveItemClick={() => onRemoveUser(user.apid!, NeededPersonRole.Approver)}
                upDownButtons={renderUpDownButtons()}
            />
        );
    }

    return (
        <SeaShellBox component="form" spacing="10">
            <Text width="100%" fontWeight="bold">{localtrans('SignatureFileTitle')}</Text>

            <VStack width="100%">
                <Text width="100%">{localtrans('SignatureTitleField')}<sup style={{color:"red"}}> *</sup></Text>
                <G1Input component="form" name="titleSession" placeholder={localtrans('SignatureTitleFieldPlaceholder')} defaultValue={session.title} onChange={onChangeTitle} />
            </VStack>
            <VStack width="100%">
                <Text width="100%">{localtrans('SignatureObjectField')}</Text>
                <Textarea
                    placeholder={localtrans('SignatureObjectPlaceholder')}
                    name="object"
                    defaultValue={session.description}
                    bg={$g1Color('form.field.bg')}
                    color={$g1Color('form.field.write')}
                    borderColor={$g1Color('form.border')}
                    _hover={{ borderColor: $g1Color('form.field.hover.border') }}
                    _focus={{ borderColor: $g1Color('form.field.select.border') }}
                    _placeholder={{ color: $g1Color('form.field.placeholder') }}
                    onChange={onChangeObject}
                ></Textarea>
            </VStack>

            {displayCalendar &&
                <VStack width="100%">
                    <Text width="100%">{localtrans('SignatureCalendarField')}<sup style={{color:"red"}}> *</sup></Text>
                    <G1DateInput component="form" name="expiredAt" dateValue={endDate} mindate={startDate.dateByAddingDays(MIN_DURING_TIME)} maxdate={startDate.dateByAddingDays(MAX_DURING_TIME)} onDateChange={onChangeDate} />
                </VStack>
            }
            {displayDuringTime &&
                <HStack width="100%">
                    <Text>{localtrans('SignatureDaysField')}</Text>
                    <NumberInput value={duringTime} min={MIN_DURING_TIME} max={MAX_DURING_TIME} step={1} onChange={onChangeDuringTime}
                        minWidth="70px"
                        width="70px"
                        bg={$g1Color(`form.field.bg`)}
                        color={$g1Color(`form.field.write`)}
                        borderColor={$g1Color(`form.field.border`)}
                        _hover={{ borderColor: $g1Color(`form.field.hover.border`) }}
                        _focus={{ borderColor: $g1Color(`form.field.select.border`) }}>
                        <NumberInputField />
                        <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                        </NumberInputStepper>
                    </NumberInput>
                </HStack>
            }
            <VStack width="100%" alignItems="normal">
                <Text>Options</Text>
                <Wrap spacingX={10} spacingY={5} rounded="xl" backgroundColor="gray.200" overflow="hidden" padding="3">
                    <WrapItem>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Switch size="lg" isChecked={optionInvisibleSwitchChecked} onChange={onToggleInvisibleSwitch} />
                        <Text fontSize='large' color={optionInvisibleSwitchChecked ? 'black' : 'blackAlpha.500'}>{localtrans('SignatureInvisibilitySwitch')}</Text>
                    </Box>
                    </WrapItem>
                    <WrapItem>
                    <Box display={!!enableAddApproversOption ? "flex" : "none"} alignItems="center" gap={2}>
                        <Switch size='lg' isChecked={optionAddApproversSwitchChecked} onChange={onToggleAddApproversSwitch} />
                        <HStack>
                            <Text fontSize='large' color={optionAddApproversSwitchChecked ? 'black' : 'blackAlpha.500'}>{localtrans('SignatureBox.ApprovalOption')}</Text>
                            <Text fontSize='small' cursor="pointer" onClick={() => toast(defaultInfoToast(localtrans('SignatureBox.AddApproverDescription')))}> ℹ️</Text>
                        </HStack>
                    </Box>
                    </WrapItem>
                    <WrapItem>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Switch size='lg' isChecked={optionOneToManyModeSwitchChecked} onChange={onToggleOneToManyModeSwitch} />
                        <HStack>
                            <Text fontSize='large' color={optionOneToManyModeSwitchChecked ? 'black' : 'blackAlpha.500'}>{localtrans('SignatureBox.OneToManyOption')}</Text>
                            <Text fontSize='small' cursor="pointer" onClick={() => toast(defaultInfoToast(localtrans('SignatureBox.OneToManyModeDescription')))}> ℹ️</Text>
                        </HStack>
                    </Box>
                    </WrapItem>
                </Wrap>
            </VStack>
            <VStack width="100%">
                <Text width="100%">{localtrans('SignatureExpeditorSection')}<sup style={{color:"red"}}> *</sup></Text>
                <SectionBox items={[session.expeditor]} sectionStyle={sectionStyle.expeditors} renderItem={_renderSenderItem} />
            </VStack>
            {optionAddApproversSwitchChecked &&
                <VStack width="100%">
                    <HStack spacing={4} width="100%">
                        <Text>{localtrans('SignatureBox.ApproversSection')}<sup style={{color:"red"}}> *</sup></Text>
                        <Box fontSize="small" borderRadius={5} borderWidth={1} borderStyle="solid" borderColor={$count(session.approvals) === config.app.nbMaxApprovers ? "red" : "darkgray"} padding="0 5px" color={$count(session.approvals) === config.app.nbMaxApprovers ? "red" : "darkgray"}>{$count(session.approvals)} / {config.app.nbMaxApprovers}</Box>
                    </HStack>
                    <SectionBox
                        items={session.approvals?.map(a => a.approver)}
                        sectionStyle={sectionStyle.signers}
                        parent={session}
                        renderItem={_renderApproverItem}
                        renderFooter={() => _renderAddButton(localtrans('SignatureBox.AddApproverButton'), $count(session.approvals) === config.app.nbMaxApprovers, () => onAddUser(NeededPersonRole.Approver))}
                    />
                </VStack>
            }
            <VStack width="100%">
                <HStack spacing={4} width="100%">
                    <Text>{localtrans('SignatureSignersSection')}<sup style={{color:"red"}}> *</sup></Text>
                    <Box fontSize="small" borderRadius={5} borderWidth={1} borderStyle="solid" borderColor={$count(session.signatures) === config.app.nbMaxSigners ? "red" : "darkgray"} padding="0 5px" color={$count(session.signatures) === config.app.nbMaxSigners ? "red" : "darkgray"}>{$count(session.signatures)} / {config.app.nbMaxSigners}</Box>
                </HStack>
                <SignaturesManager signatures={session?.signatures} limit={config.app.nbMaxSigners} soloMode={optionOneToManyModeSwitchChecked} sectionStyle={sectionStyle.signers} itemStyle={itemStyle.signer} onSignersChange={onChangeSignatures} onRemoveSigner={onRemoveSigner} onAddSigner={() => onAddUser(NeededPersonRole.Signer)} />
            </VStack>
        </SeaShellBox>
    );
};

const _renderAddButton = (title: string, disabled: boolean, click: () => void) => {
    return <SectionBoxFooter component="contacts" title={title} disabled={disabled} onClick={click} /> ;
};